import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import qs from "qs";
import { ThemeContext } from "styled-components";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {
  useMediaQuery,
  Flex,
  Toast,
  Skeleton,
} from "discover.medical.react-component-library";
import { useHistory, useLocation } from "react-router-dom";

import { useStore, useDispatch } from "../../store";
import {
  fetchSuggestionResults,
  clearSuggestionResults,
  fetchToolBarConfig,
} from "../../store/search-bar/actions";
import { useQueryParams } from "../../hooks/useReadableQueryParams";
import SearchOtherLanguagesDropdown from "../SearchOtherLanguagesDropdown";
import {
  AutocompleteWrapper,
  AutoSuggestMenuItemMatchWrapper,
  StyledAutocompleteBar,
} from "./SearchAutoSuggest.styled";
import { AFFORDANCE, logSearchInitiated } from "../../util/analytics";
import { setLangCookie, getLangCookie } from "../../shared/_functions";
import { resetFastFeedback } from "../../util/fastFeedbackHelper";
import ErrorBoundary from "../ErrorBoundary";

const DEFAULT_LANG = "en";

const executeSearch = ({
  term,
  language,
  affordance,
  options,
  history,
  location,
}) => {
  logSearchInitiated({
    affordance,
    searchTerm: term,
    language: language || DEFAULT_LANG,
  });
  const searchParam = options.param;
  const search = {};
  search[searchParam] = term;
  const pathnameWithBasePath = options.route;
  if (language) {
    search.lang = language;
  }
  const routerState = { ...location?.state, initiatedBy: "autocomplete" };

  const searchRoute = {
    pathname: pathnameWithBasePath,
    search: qs.stringify(search),
    state: routerState,
  };
  history.push(searchRoute);
};

const SearchAutoSuggest = (props) => {
  const [queryParams] = useQueryParams();
  const [typedTerm, setTypedTerm] = useState(queryParams.q || "");
  const [highlightedTerm, setHighlightedTerm] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState({
    key: DEFAULT_LANG,
    title: "English",
    text: "Search",
  });
  const [toast, setToast] = useState(true);
  const themeContext = useContext(ThemeContext);
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();

  useEffect(() => {
    fetchToolBarConfig(dispatch);
  }, [dispatch]);

  useEffect(() => {
    const { pathname, search, hash, state } = location;

    // we want to handle refreshing toolbar config to handle adding new stuff to display like recently viewed
    if (state?.shouldReloadRecentlyViewed) {
      const rebuiltPath = `${pathname}${search}${hash}`;
      fetchToolBarConfig(dispatch);
      history.replace(rebuiltPath, {
        ...state,
        shouldReloadRecentlyViewed: false,
      });
    }
  }, [location, history, dispatch]);

  const { searchOptions } = store?.config?.actions || {};
  const { languages = [], buttonOptions, defaultLanguageKey } =
    store?.config?.languageOptions || {};
  const { suggestions } = store.autoSuggestions;
  const { hasFetched, recentlyViewed } = store.config;
  const error = store.config.error;

  // Get index of the language selected from the cookie
  const getLangIndex = (langKey) =>
    languages?.findIndex((language) => language.key === langKey);

  // Default index to english if the cookie doesn't exist or is tampered with unidentified language key
  const defaultIndex =
    getLangIndex(getLangCookie()) === -1
      ? getLangIndex(defaultLanguageKey)
      : getLangIndex(getLangCookie());

  const onChange = (event, { newValue: newTerm, method }) => {
    setHighlightedTerm(newTerm);
    if (method === "type") {
      setTypedTerm(newTerm);
    }
  };

  const onCancel = () => {
    setTypedTerm("");
    setHighlightedTerm("");
  };

  const handleSuggestionsFetchRequested = (newTerm) => {
    const termLength = newTerm?.value?.trim()?.length;
    setTypedTerm(newTerm.value?.trim());
    if (termLength > 0 && termLength < 200) {
      fetchSuggestionResults(dispatch, newTerm.value);
    }
  };

  const handleSuggestionsClearRequested = () => {
    clearSuggestionResults(dispatch);
  };

  const onSearchClick = (event) => {
    event.preventDefault();
    resetFastFeedback();
    if (typedTerm && typedTerm.trim()?.length > 0) {
      // We are plucking the current language key from state to include it in the query
      const { key } = languages[defaultIndex] || selectedLanguage;
      executeSearch({
        term: typedTerm,
        language: key,
        history,
        affordance: AFFORDANCE.manual,
        options: searchOptions,
        location,
      });
    }
  };

  const onSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    document.activeElement.blur();
    setLangCookie();
    // Go to the article with the given title
    const contentSlug = suggestion?.resource?.slug;
    if (contentSlug) {
      logSearchInitiated({
        affordance:
          typedTerm.length > 0 ? AFFORDANCE.goTo : AFFORDANCE.recentlyViewed,
        searchTerm: contentSlug,
        language: DEFAULT_LANG,
      });
      history.push({ pathname: contentSlug });
      // Show all matches for the typedTerm entered
    } else if (event.key === "Enter") {
      const eventTerm = event?.currentTarget?.value;
      event.preventDefault();
      setTypedTerm(eventTerm);
      if (eventTerm) {
        executeSearch({
          term: eventTerm,
          history,
          affordance: AFFORDANCE.searchFor,
          options: searchOptions,
          location,
        });
      }
      // Show all results matching a typedTerm
    } else if (suggestionValue?.length) {
      executeSearch({
        term: suggestionValue,
        history,
        affordance: AFFORDANCE.searchFor,
        options: searchOptions,
        location,
      });
    }
    resetFastFeedback();
  };

  const getSuggestionValue = (suggestion) => suggestion.completedPhrase;

  const renderSuggestion = (suggestion) => {
    const matches = match(suggestion.completedPhrase, typedTerm);
    const parts = parse(suggestion.completedPhrase, matches);

    return (
      <AutoSuggestMenuItemMatchWrapper data-auto="auto-complete-suggestion">
        {parts.map((part, index) => {
          const className = part.highlight ? "match" : "noMatch";
          return (
            <span className={className} key={index}>
              {part.text}
            </span>
          );
        })}
      </AutoSuggestMenuItemMatchWrapper>
    );
  };

  function renderSectionTitle(section) {
    return <strong>{section.title}</strong>;
  }

  function getSectionSuggestions(section) {
    return section.items;
  }

  const getInputSize = (size) => {
    if (size) {
      return size;
    } else {
      return smMq ? "md" : "sm";
    }
  };

  const currentLanguage = languages?.[defaultIndex]?.key || DEFAULT_LANG;
  const shouldRenderSuggestions = () =>
    typedTerm.trim().length > 0
      ? currentLanguage === DEFAULT_LANG
      : recentlyViewed?.items.length > 0;

  const defaultLanguageText = languages[defaultIndex]
    ? languages[defaultIndex].text
    : "Search";

  const isIconOnly = (buttonOptions, smMq, mdMq) => {
    if (buttonOptions) {
      let settings = buttonOptions.secondary.iconOnly;
      if (location.pathname !== "/") {
        settings = buttonOptions.iconOnly;
      }
      if (mdMq) {
        return settings.lg;
      }
      if (smMq) {
        return settings.md;
      }
      return settings.sm;
    }
  };

  const smMq = useMediaQuery("min", "sm");
  const mdMq = useMediaQuery("min", "md");
  const inputSize = getInputSize(props.size);
  const inputProps = {
    // We will use the correct placeholder text we get back from languagesConfig
    placeholder: defaultLanguageText,
    value: highlightedTerm || typedTerm,
    onCancel: onCancel,
    onChange: onChange,
    size: inputSize,
    name: "search",
    type: "search",
    id: "autosuggest",
    lang: currentLanguage,
    cancelable: true,
    style: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      fontSize: !smMq && themeContext.font.size.md,
    },
  };

  const handleClose = () => {
    setToast(false);
  };

  if (error !== undefined) {
    return (
      <Toast
        data-testid="toolbar-error-toast"
        isOpen={toast}
        onClose={handleClose}
        type="error"
        children="Unexpected error occurred. Please refresh and try again."
      />
    );
  }

  return (
    <ErrorBoundary
      team="cd.ghostriders"
      errorMsgTestId="search-auto-suggest-failure-message"
    >
      {store?.config?.hasFetched ? (
        store.config.actions ? (
          <Flex container data-testid="search-toolBar">
            <SearchOtherLanguagesDropdown
              supportedLanguages={languages}
              defaultIndex={defaultIndex}
              hasFetched={hasFetched}
              setSelectedLanguage={setSelectedLanguage}
              size={inputSize}
              showTitle={!isIconOnly(buttonOptions, smMq, mdMq)}
            />
            <AutocompleteWrapper
              role="search"
              onSubmit={(e) => {
                onSearchClick(e);
              }}
            >
              <StyledAutocompleteBar
                data-testid="styledAutoComplete"
                inputProps={inputProps}
                suggestions={!typedTerm ? [recentlyViewed] : suggestions}
                onSearchClick={onSearchClick}
                onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
                onSuggestionsClearRequested={handleSuggestionsClearRequested}
                getSuggestionValue={getSuggestionValue}
                getSectionSuggestions={getSectionSuggestions}
                onSuggestionSelected={onSuggestionSelected}
                renderSuggestion={renderSuggestion}
                shouldRenderSuggestions={shouldRenderSuggestions}
                search={true}
                multiSection={true}
                renderSectionTitle={renderSectionTitle}
                focusInputOnSuggestionClick={false}
              />
            </AutocompleteWrapper>
          </Flex>
        ) : (
          <Toast
            data-testid="toolbar-toast"
            isOpen={toast}
            onClose={handleClose}
            type="error"
            children="Unexpected error occurred. Please refresh and try again."
          />
        )
      ) : (
        <Skeleton kind="rect" animate />
      )}
    </ErrorBoundary>
  );
};
SearchAutoSuggest.displayName = "SearchAutoSuggest";
SearchAutoSuggest.propTypes = {
  size: PropTypes.string,
};
export default SearchAutoSuggest;
